var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { UserType } from "healthex-common/user.dtos";
import { willNeverHappen } from "healthex-common/utils";
import { computed } from "mobx";
import { NOT_LOADED } from "../../Infra/RemoteData";
import { NamedRoutes } from "../NamedRoutes";
import { NavigationHelper } from "../NavigationHelper";
import { findMatchingNamedRouteParent } from "../RoutingUIManager";
const globalAdminTabToRoutes = {
    ORGANIZATIONS: NamedRoutes.GLOBAL_ADMIN_ORGANIZATIONS,
    AI_PARTNERS: NamedRoutes.GLOBAL_ADMIN_AI_PARTNERS,
    MEDALLIES: NamedRoutes.GLOBAL_ADMIN_MEDALLIES,
    JOBS: NamedRoutes.GLOBAL_ADMIN_JOB_MANAGEMENT,
    DOCUMENT_RETRIEVALS: NamedRoutes.GLOBAL_ADMIN_DOCUMENT_RETRIEVALS,
    BILLING: NamedRoutes.GLOBAL_ADMIN_BILLING,
};
const globalAdminHealthcareOrganizationPageToRoutes = {
    MY_ORG: NamedRoutes.GLOBAL_ADMIN_ORG_DATA_ORIGINATOR_DETAIL,
    LOGIN_SETTINGS: NamedRoutes.GLOBAL_ADMIN_ORG_DATA_ORIGINATOR_LOGIN_SETTINGS,
    MY_USERS: NamedRoutes.GLOBAL_ADMIN_ORG_DATA_ORIGINATOR_USERS,
    PATIENT_SETTINGS: NamedRoutes.ADMIN_PATIENT_SETTINGS,
    MY_API_KEYS: NamedRoutes.ADMIN_APIKEYS,
};
const globalAdminExternalResearcherOrganizationPageToRoutes = {
    MY_ORG: NamedRoutes.GLOBAL_ADMIN_ORG_DATA_USER_DETAIL,
    LOGIN_SETTINGS: NamedRoutes.INDEX,
    MY_USERS: NamedRoutes.GLOBAL_ADMIN_ORG_DATA_USER_USERS,
    PATIENT_SETTINGS: NamedRoutes.INDEX,
    MY_API_KEYS: NamedRoutes.ADMIN_APIKEYS,
};
const globalAdminServiceProviderPageToRoutes = {
    MY_ORG: NamedRoutes.GLOBAL_ADMIN_ORG_SERVICE_PROVIDER_DETAIL,
    LOGIN_SETTINGS: NamedRoutes.INDEX,
    MY_USERS: NamedRoutes.GLOBAL_ADMIN_ORG_SERVICE_PROVIDER_USERS,
    PATIENT_SETTINGS: NamedRoutes.INDEX,
    MY_API_KEYS: NamedRoutes.ADMIN_APIKEYS,
};
export class GlobalAdminUIManager {
    constructor(routingUIManager, authManager, invalidationManager, organizationsManager, consentConfigManager) {
        this.routingUIManager = routingUIManager;
        this.authManager = authManager;
        this.invalidationManager = invalidationManager;
        this.organizationsManager = organizationsManager;
        this.consentConfigManager = consentConfigManager;
        this.tabNavigationHelper = new NavigationHelper(this.routingUIManager, globalAdminTabToRoutes);
        this.globalAdminHealthcareOrganizationNavigationHelper = new NavigationHelper(this.routingUIManager, globalAdminHealthcareOrganizationPageToRoutes);
        this.globalAdminExternalResearcherOrganizationNavigationHelper = new NavigationHelper(this.routingUIManager, globalAdminExternalResearcherOrganizationPageToRoutes);
        this.globalAdminServiceProviderNavigationHelper = new NavigationHelper(this.routingUIManager, globalAdminServiceProviderPageToRoutes);
        this.routingUIManager.registerRedirect(NamedRoutes.GLOBAL_ADMIN, () => {
            return this.tabNavigationHelper.navigationItemToRoute("ORGANIZATIONS");
        });
    }
    get currentTab() {
        return this.tabNavigationHelper.currentNavigationItem;
    }
    setCurrentTab(tab) {
        this.tabNavigationHelper.navigateTo(tab);
    }
    get tabsToShow() {
        return [
            { tab: "ORGANIZATIONS", label: "Organizations" },
            { tab: "AI_PARTNERS", label: "AI Partners" },
            { tab: "MEDALLIES", label: "MedAllies" },
            { tab: "JOBS", label: "Jobs" },
            { tab: "DOCUMENT_RETRIEVALS", label: "Retrievals" },
            { tab: "BILLING", label: "Billing" },
        ];
    }
    get organization() {
        var _a, _b;
        if (this.authManager.userType !== UserType.HEALTHCARE_ORGANIZATION)
            return null;
        if (this.organizationIdToAdmin === null)
            return null;
        if (this.organizationsManager.organizations.value === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.organizationsManager.organizations.value) === null || _a === void 0 ? void 0 : _a.find((candidate) => candidate.id === this.organizationIdToAdmin)) !== null && _b !== void 0 ? _b : null;
    }
    async updateHealthcareOrganizationDetails(update) {
        var _a;
        if (this.authManager.userType !== UserType.HEALTHCARE_ORGANIZATION)
            return;
        if (this.organizationIdToAdmin === null)
            return;
        if (this.organization === NOT_LOADED || this.organization === null)
            return;
        await this.organizationsManager.updateOrganizationDetails(this.organizationIdToAdmin, update);
        await ((_a = this.organizationsManager.organizations) === null || _a === void 0 ? void 0 : _a.waitForReload());
    }
    get adminType() {
        if (!this.routingUIManager.currentRoute)
            return null;
        const matchingRoute = findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [
            NamedRoutes.ADMIN,
            NamedRoutes.GLOBAL_ADMIN_ORG_DATA_ORIGINATOR_DETAIL,
            NamedRoutes.GLOBAL_ADMIN_ORG_DATA_USER_DETAIL,
            NamedRoutes.GLOBAL_ADMIN_ORG_SERVICE_PROVIDER_DETAIL,
        ]);
        if (!matchingRoute)
            return null;
        if (matchingRoute.routeName === NamedRoutes.GLOBAL_ADMIN_ORG_DATA_ORIGINATOR_DETAIL.routeName) {
            return "GLOBAL_HEALTHCARE_ORGANIZATION";
        }
        else if (matchingRoute.routeName === NamedRoutes.GLOBAL_ADMIN_ORG_DATA_USER_DETAIL.routeName) {
            return "GLOBAL_EXTERNAL_RESEARCHER";
        }
        else if (matchingRoute.routeName === NamedRoutes.GLOBAL_ADMIN_ORG_SERVICE_PROVIDER_DETAIL.routeName) {
            return "GLOBAL_SERVICE_PROVIDER";
        }
        else {
            return null;
        }
    }
    get navigationHelper() {
        if (!this.adminType)
            return null;
        switch (this.adminType) {
            case "GLOBAL_HEALTHCARE_ORGANIZATION":
                return this.globalAdminHealthcareOrganizationNavigationHelper;
            case "GLOBAL_EXTERNAL_RESEARCHER":
                return this.globalAdminExternalResearcherOrganizationNavigationHelper;
            case "GLOBAL_SERVICE_PROVIDER":
                return this.globalAdminServiceProviderNavigationHelper;
        }
        return willNeverHappen(this.adminType);
    }
    get currentAdminPage() {
        var _a, _b;
        return (_b = (_a = this.navigationHelper) === null || _a === void 0 ? void 0 : _a.currentNavigationItem) !== null && _b !== void 0 ? _b : null;
    }
    setCurrentAdminPage(adminPage) {
        var _a, _b;
        const routeParams = { orgId: (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.orgId };
        (_b = this.navigationHelper) === null || _b === void 0 ? void 0 : _b.navigateTo(adminPage, routeParams);
    }
    get organizationIdToAdmin() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.orgId) !== null && _b !== void 0 ? _b : null;
    }
    get organizationDetailsAreEditable() {
        if (this.authManager.orgId === this.organizationIdToAdmin) {
            return true;
        }
        else {
            return this.authManager.userType === "SERVICE_PROVIDER" && this.organizationTypeToAdmin !== "SERVICE_PROVIDER";
        }
    }
    get organizationTypeToAdmin() {
        if (!this.adminType)
            return null;
        switch (this.adminType) {
            case "GLOBAL_HEALTHCARE_ORGANIZATION":
                return UserType.HEALTHCARE_ORGANIZATION;
            case "GLOBAL_EXTERNAL_RESEARCHER":
                return UserType.EXTERNAL_RESEARCHER;
            case "GLOBAL_SERVICE_PROVIDER":
                return UserType.SERVICE_PROVIDER;
        }
        return willNeverHappen(this.adminType);
    }
    get canCreateUserInOrganizationToShowUserDetailsFor() {
        return this.authManager.orgId === this.organizationIdToAdmin;
    }
    get userIdToShowDetailsFor() {
        var _a, _b;
        if (!this.adminType) {
            return null;
        }
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.userId) !== null && _b !== void 0 ? _b : null;
    }
    get keyIdToShowDetailsFor() {
        var _a, _b;
        if (!this.adminType) {
            return null;
        }
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.keyId) !== null && _b !== void 0 ? _b : "null";
    }
    get loginSettingsAreEditable() {
        return this.authManager.orgId === this.organizationIdToAdmin;
    }
    get userDetailsAreEditable() {
        return this.authManager.orgId === this.organizationIdToAdmin;
    }
    navigateToUserDetails(userId) {
        if (!this.organizationIdToAdmin)
            return;
        if (this.adminType === "GLOBAL_HEALTHCARE_ORGANIZATION") {
            this.routingUIManager.navigateTo(NamedRoutes.GLOBAL_ADMIN_ORG_DATA_ORIGINATOR_USER_DETAIL, {
                userId,
                orgId: this.organizationIdToAdmin,
            });
        }
        else if (this.adminType === "GLOBAL_EXTERNAL_RESEARCHER") {
            this.routingUIManager.navigateTo(NamedRoutes.GLOBAL_ADMIN_ORG_DATA_USER_USER_DETAIL, {
                userId,
                orgId: this.organizationIdToAdmin,
            });
        }
        else if (this.adminType === "GLOBAL_SERVICE_PROVIDER") {
            this.routingUIManager.navigateTo(NamedRoutes.GLOBAL_ADMIN_ORG_SERVICE_PROVIDER_USER_DETAIL, {
                userId,
                orgId: this.organizationIdToAdmin,
            });
        }
    }
}
__decorate([
    computed
], GlobalAdminUIManager.prototype, "currentTab", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "tabsToShow", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "organization", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "adminType", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "navigationHelper", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "currentAdminPage", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "organizationIdToAdmin", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "organizationTypeToAdmin", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "userIdToShowDetailsFor", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "keyIdToShowDetailsFor", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "loginSettingsAreEditable", null);
__decorate([
    computed
], GlobalAdminUIManager.prototype, "userDetailsAreEditable", null);
